import { takeLatest, takeEvery, delay, select, call, put, take, race, spawn, cancelled } from 'redux-saga/effects';
import * as R from 'ramda';
import {
  account as accountApis,
  generativeAi as generativeAiApis,
  ti2 as ti2Apis,
  matcher as matcherApis,
} from '../../../../../api.js';
import { apiSaga } from '../../../../../root-sagas.js';
import { _getAccountInfo } from '../../../__store__/partner-settings/edit-app-key-configuration-sagas.js';
import { SUPPORTED_JOB_STATUSES, TERMINAL_JOB_STATUSES } from '../gondolaJobStatus';
import { ACTIVE_INGEST_STATUSES, isActiveIngest, isPendingManualSelection } from '../gondolaUtils';

export function* onLocationChange({ payload }) {
  const pathname = R.pathOr('', ['location', 'pathname'], payload);
  const m = pathname.match(/\/context-management\/([^/]+)(?:\/scope\/([^/]+))?(?:\/ingestion(?:\/([^/]+))?)?/);
  if (!m) {
    yield put({ type: 'gondola_leaveScope' });
    return;
  }
  const scopeId = m[2];
  const ingestionMatch = pathname.match(/\/context-management\/[^/]+\/ingestion(?:\/([^/]+))?/);
  const onIngestionTab = Boolean(ingestionMatch);
  const reviewIngestId = ingestionMatch ? ingestionMatch[1] : null;

  const { companyId } = yield call(_getAccountInfo);
  if (!companyId) return;

  if (scopeId) {
    yield put({
      type: 'gondola_enterScope',
      payload: { scopeId, contextScope: `${companyId}/${scopeId}` },
    });
  } else {
    yield put({ type: 'gondola_leaveScope' });
    yield spawn(function* () {
      yield apiSaga('gondola_scopes', generativeAiApis.gondolaGetScopes, [companyId, { skipLoader: true }]);
    });
  }
  yield put({ type: 'gondola_pollBatchPromotionStatus' });
  yield spawn(function* () {
    yield apiSaga('gondola_deltaSyncStatus', generativeAiApis.gondolaGetDeltaSyncStatus, [{ companyId, skipLoader: true }]);
    const inProgress = yield select(R.pathOr(false, ['generativeAi', 'gondola', 'deltaSyncStatus', 'inProgress']));
    const alreadyPolling = yield select(R.pathOr(false, ['generativeAi', 'gondola', 'deltaSyncPolling']));
    // Avoid takeLatest cancel/restart — that drops completion-refresh state mid-sync.
    if (inProgress && !alreadyPolling) {
      yield put({ type: 'gondola_pollDeltaSyncStatus' });
    }
  });

  const existingAppKeys = yield select(R.pathOr([], ['generativeAi', 'gondola', 'appKeys']));
  if (!existingAppKeys.length) {
    yield put({ type: 'gondola_fetchAppKeys' });
  }
  const existingPartners = yield select(R.pathOr([], ['PM', 'partnerList', 'list', 'partners']));
  if (!existingPartners.length) {
    yield put({ type: '@partner-list/GET_PARTNERS' });
  }

  if (onIngestionTab) {
    yield put({ type: 'gondola_fetchIngests', payload: { companyId } });
    if (reviewIngestId) {
      yield put({ type: 'gondola_fetchIngestPartners', payload: { ingestId: reviewIngestId, companyId } });
      yield put({ type: 'gondola_fetchIngestEmails', payload: { ingestId: reviewIngestId, companyId } });
    }
  }
}

export function* pollActiveJob(contextScope, celeryJobId) {
  if (typeof contextScope !== 'string' || !celeryJobId) return;
  const contextParts = contextScope.split('/');
  const companyId = contextParts[0];
  const scopeId = contextParts[contextParts.length - 1];
  if (!companyId || !scopeId) return;

  while (true) {
    let owner = yield select(scopeOwnerSelector);
    if (!isScopeOwner(owner, companyId, scopeId, contextScope)) return;

    const scopeDetail = yield apiSaga('gondola_scopeDetail', generativeAiApis.gondolaGetScopeDetail, [
      scopeId,
      { contextScope, skipLoader: true },
    ]);
    owner = yield select(scopeOwnerSelector);
    if (!isScopeOwner(owner, companyId, scopeId, contextScope)) return;
    const durableStatus = String(R.pathOr('', ['activity', 'status'], scopeDetail)).toLowerCase();
    if (durableStatus === 'running' && R.path(['activity', 'phase'], scopeDetail) === 'queued') {
      yield delay(10e3);
      continue;
    }
    if (durableStatus === 'paused') {
      yield apiSaga('gondola_scopes', generativeAiApis.gondolaGetScopes, [companyId, { skipLoader: true }]);
      owner = yield select(scopeOwnerSelector);
      if (!isScopeOwner(owner, companyId, scopeId, contextScope)) return;
      yield put({ type: 'gondola_fetchScopeRuns', payload: { scopeId, contextScope, limit: 30 } });
      yield put({ type: 'gondola_fetchScopeCounts', payload: { scopeId, contextScope } });
      break;
    }

    let jobResp = null;
    try {
      const { response } = yield call(matcherApis.checkStatus, {
        jobs: [{ jobId: celeryJobId, jobType: 'pyFileMatch' }],
        skipLoader: true,
      });
      jobResp = Array.isArray(response) ? response[0] : null;
    } catch (_) {
      yield delay(10e3);
      continue;
    }
    if (!jobResp) {
      yield delay(10e3);
      continue;
    }

    owner = yield select(scopeOwnerSelector);
    if (!isScopeOwner(owner, companyId, scopeId, contextScope)) return;

    yield put({
      type: 'gondola_jobProgress',
      payload: { jobResp, celeryJobId, contextScope },
    });

    const status = typeof jobResp.status === 'string' ? jobResp.status.toLowerCase() : '';
    const unsupportedStatus = Boolean(status && !SUPPORTED_JOB_STATUSES.has(status));
    if (jobResp.isTerminal === true || TERMINAL_JOB_STATUSES.has(status) || unsupportedStatus) {
      owner = yield select(scopeOwnerSelector);
      if (!isScopeOwner(owner, companyId, scopeId, contextScope)) return;
      yield apiSaga('gondola_scopes', generativeAiApis.gondolaGetScopes, [companyId, { skipLoader: true }]);
      owner = yield select(scopeOwnerSelector);
      if (!isScopeOwner(owner, companyId, scopeId, contextScope)) return;
      yield apiSaga('gondola_scopeDetail', generativeAiApis.gondolaGetScopeDetail, [
        scopeId,
        { contextScope, skipLoader: true },
      ]);
      yield put({ type: 'gondola_fetchScopeRuns', payload: { scopeId, contextScope, limit: 30 } });
      yield put({ type: 'gondola_fetchScopeCounts', payload: { scopeId, contextScope } });
      break;
    }
    yield delay(10e3);
  }
}

function* handlePollActiveJob({ payload }) {
  const { contextScope, celeryJobId } = payload || {};
  if (!contextScope || !celeryJobId) return;
  yield race({
    poll: call(pollActiveJob, contextScope, celeryJobId),
    cancel: take(['gondola_leaveScope', 'gondola_enterScope']),
  });
}

export function* handleEnterScope({ payload }) {
  const { scopeId, contextScope: requestedContextScope } = payload;
  const companyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
  if (!companyId) return;
  const contextScope = `${companyId}/${scopeId}`;
  if (requestedContextScope && requestedContextScope !== contextScope) return;

  yield apiSaga('gondola_scopeDetail', generativeAiApis.gondolaGetScopeDetail, [
    scopeId,
    { contextScope, skipLoader: true },
  ]);
  let owner = yield select(scopeOwnerSelector);
  if (!isScopeOwner(owner, companyId, scopeId, contextScope)) return;

  yield put({ type: 'gondola_fetchScopeMapping', payload: { scopeId, contextScope } });
  yield put({ type: 'gondola_fetchScopeRuns', payload: { scopeId, contextScope, limit: 30 } });
  yield put({ type: 'gondola_fetchScopeFiles', payload: { scopeId, contextScope } });
  yield put({ type: 'gondola_fetchScopeCounts', payload: { scopeId, contextScope } });

  const activity = yield select(R.path(['generativeAi', 'gondola', 'currentScope', 'activity']));
  const celeryJobId = R.prop('celeryJobId', activity);
  const activityStatus = String(R.propOr('', 'status', activity)).toLowerCase();
  if (!celeryJobId || activityStatus === 'paused') return;

  yield race({
    poll: call(pollActiveJob, contextScope, celeryJobId),
    cancel: take(['gondola_leaveScope', 'gondola_enterScope']),
  });
}

function* fetchScopes({ payload }) {
  yield apiSaga('gondola_scopes', generativeAiApis.gondolaGetScopes, [payload.companyId, { skipLoader: true }]);
}

function* enrichTi2s(ti2s) {
  const { ti2token } = yield call(_getAccountInfo);
  const url = process.env.REACT_APP_TI2_URL;
  return (ti2s || []).map(t => ({ ...t, url, token: ti2token }));
}

function* createScope({ payload }) {
  yield apiSaga('gondola_createScope', generativeAiApis.gondolaCreateScope, [payload, { skipLoader: true }]);
  yield put({ type: 'gondola_fetchScopes', payload: { companyId: payload.companyId } });
}

export function* saveScopeAutomation({ payload }) {
  const result = yield apiSaga(
    'gondola_saveScopeAutomation',
    generativeAiApis.saveAISettings,
    [{ gondola: { createScopePerAgent: Boolean(payload.createScopePerAgent) } }],
  );
  if (result) {
    yield apiSaga('@app/GET_ACCOUNT_INFO', accountApis.getAccountInfo, []);
  }
}

export function* fetchIngests({ payload }) {
  const result = yield apiSaga('gondola_ingests', generativeAiApis.gondolaGetIngests, [payload.companyId, { skipLoader: true }]);
  const ingests = Array.isArray(result && result.ingests) ? result.ingests : [];
  const hasRunning = ingests.some(isActiveIngest);
  if (hasRunning) {
    yield put({ type: 'gondola_pollIngests', payload: { companyId: payload.companyId } });
  }
}

const INGEST_UPLOAD_TIMEOUT_MS = 120 * 1000;

export function uploadIngestFiles(urlEntries, files, signal) {
  return Promise.all(
    urlEntries.map(async ({ filename, fileIndex, uploadUrl }) => {
      const hasStableIndex = Number.isSafeInteger(fileIndex) && fileIndex >= 0;
      const file = hasStableIndex ? files[fileIndex] : files.find(f => f.name === filename);
      if (!file || (hasStableIndex && file.name !== filename)) {
        throw new Error(`Missing file for ${filename}`);
      }
      const controller = new AbortController();
      const abortTimer = setTimeout(() => controller.abort(), INGEST_UPLOAD_TIMEOUT_MS);
      const onParentAbort = () => controller.abort();
      if (signal) {
        if (signal.aborted) controller.abort();
        else signal.addEventListener('abort', onParentAbort, { once: true });
      }
      let response;
      try {
        response = await fetch(uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'message/rfc822' },
          body: file,
          signal: controller.signal,
        });
      } finally {
        clearTimeout(abortTimer);
        if (signal) signal.removeEventListener('abort', onParentAbort);
      }
      if (!response.ok) {
        throw new Error(`Failed to upload ${filename}`);
      }
    }),
  );
}

const isStopForIngest = ingestId => action => (
  action.type === 'gondola_stopIngest'
  && R.path(['payload', 'ingestId'], action) === ingestId
);

function* putStartIngestionError(message) {
  yield put({
    type: 'gondola_startIngestion_ERROR',
    payload: message,
    args: [{ skipLoader: true }],
  });
  yield put({
    type: 'global_openSnackBar',
    payload: {
      snackbarMessage: message,
      snackBarSeverity: 'error',
    },
  });
}

function* completeIngestionAfterInit({ ingestId, files, companyId, ti2s }) {
  const controller = new AbortController();
  try {
    if (files && files.length) {
      const filenames = files.map(f => f.name);
      const urlsResult = yield call(generativeAiApis.gondolaGetIngestUploadUrls, { companyId, ingestId, filenames });
      if (urlsResult.error) {
        yield* putStartIngestionError('Failed to get upload URLs');
        yield put({ type: 'gondola_fetchIngests', payload: { companyId } });
        return false;
      }
      const urlEntries = R.pathOr([], ['response', 'urls'], urlsResult);
      yield call(uploadIngestFiles, urlEntries, files, controller.signal);
    }

    const enrichedTi2s = yield call(enrichTi2s, ti2s);
    yield apiSaga('gondola_startIngest', generativeAiApis.gondolaStartIngest, [ingestId, {
      companyId, ti2s: enrichedTi2s,
      skipLoader: true,
    }]);

    yield put({ type: 'gondola_fetchIngests', payload: { companyId } });
    yield put({ type: 'gondola_pollIngests', payload: { companyId } });
    return true;
  } finally {
    if (yield cancelled()) {
      controller.abort();
    }
  }
}

export function* startIngestion({ payload }) {
  let ingestId;
  const { files, companyId, ti2s } = payload;
  try {
    const initResult = yield call(generativeAiApis.gondolaInitIngest, { companyId });
    if (initResult.error) {
      yield* putStartIngestionError('Failed to init ingest');
      return;
    }
    ingestId = R.path(['response', 'ingestId'], initResult);
    yield put({ type: 'gondola_fetchIngests', payload: { companyId } });

    const winner = yield race({
      started: call(completeIngestionAfterInit, {
        ingestId, files, companyId, ti2s,
      }),
      stopped: take(isStopForIngest(ingestId)),
    });
    if (winner.stopped) {
      yield put({
        type: 'gondola_startIngestion_CANCELLED',
        args: [{ skipLoader: true }],
      });
      yield put({ type: 'gondola_fetchIngests', payload: { companyId } });
      return;
    }
    if (winner.started) {
      yield put({
        type: 'gondola_startIngestion_SUCCESS',
        args: [{ skipLoader: true }],
      });
    }
  } catch (err) {
    console.debug('startIngestion error', err);
    const timedOut = err && err.name === 'AbortError';
    yield* putStartIngestionError(
      timedOut ? 'Upload timed out' : (err && err.message) || 'Ingestion failed',
    );
    if (ingestId) {
      yield put({ type: 'gondola_fetchIngests', payload: { companyId } });
    }
  }
}

export function* pollIngests({ payload }) {
  const { companyId } = payload;
  for (let i = 0; i < 120; i++) {
    const currentCompanyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
    if (currentCompanyId !== companyId) return;
    yield delay(5e3);
    yield apiSaga('gondola_ingests', generativeAiApis.gondolaGetIngests, [companyId, { skipLoader: true }]);
    const responseCompanyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
    if (responseCompanyId !== companyId) return;
    const ingests = yield select(R.pathOr([], ['generativeAi', 'gondola', 'ingests']));
    const hasRunning = ingests.some(isActiveIngest);
    if (!hasRunning) break;
  }
}

let ingestPartnersRequestSequence = 0;

function* fetchIngestPartners({ payload }) {
  const { ingestId, companyId } = payload;
  const requestId = ++ingestPartnersRequestSequence;
  const owner = { ingestId, companyId, requestId };
  yield put({ type: 'gondola_ingestPartners_STARTED', payload: owner, args: [{ companyId, skipLoader: true }] });
  const { response, error } = yield call(generativeAiApis.gondolaGetIngestPartners, ingestId, companyId);
  if (error) {
    yield put({ type: 'gondola_ingestPartners_ERROR', payload: { ...owner, error: error.message || error } });
  } else {
    yield put({
      type: 'gondola_ingestPartners_SUCCESS',
      payload: { ...owner, response },
      args: [{ companyId, skipLoader: true }],
    });
  }
}

let ingestEmailsRequestSequence = 0;

export function* fetchIngestEmails({ payload }) {
  const { ingestId, companyId } = payload;
  const requestId = ++ingestEmailsRequestSequence;
  const owner = { ingestId, companyId, requestId };
  const args = [{ companyId, skipLoader: true }];
  yield put({ type: 'gondola_ingestEmails_STARTED', payload: owner, args });
  const { response, error } = yield call(generativeAiApis.gondolaGetIngestEmails, ingestId, companyId);
  if (error) {
    yield put({
      type: 'gondola_ingestEmails_ERROR',
      payload: { ...owner, error: 'Unable to load ingest emails' },
      args,
    });
  } else {
    yield put({
      type: 'gondola_ingestEmails_SUCCESS',
      payload: { ...owner, response },
      args,
    });
  }
}

let ingestEmailDetailRequestSequence = 0;

export function* fetchIngestEmailDetail({ payload }) {
  const { ingestId, emailId, companyId } = payload;
  const requestId = ++ingestEmailDetailRequestSequence;
  const owner = { ingestId, emailId, companyId, requestId };
  const args = [{ companyId, skipLoader: true }];
  yield put({ type: 'gondola_ingestEmailDetail_STARTED', payload: owner, args });
  const { response, error } = yield call(
    generativeAiApis.gondolaGetIngestEmailDetail,
    ingestId,
    emailId,
    companyId,
  );
  const currentCompanyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
  if (currentCompanyId !== companyId) {
    yield put({ type: 'gondola_ingestEmailDetail_CANCELLED', payload: owner, args });
    return;
  }
  if (error) {
    yield put({
      type: 'gondola_ingestEmailDetail_ERROR',
      payload: { ...owner, error: 'Unable to load original email' },
      args,
    });
    return;
  }
  yield put({
    type: 'gondola_ingestEmailDetail_SUCCESS',
    payload: { ...owner, response },
    args,
  });
}

let ingestEmailSearchRequestSequence = 0;

export function* searchIngestEmail({ payload }) {
  const { ingestId, emailId, companyId, ti2, criteria } = payload;
  const requestId = ++ingestEmailSearchRequestSequence;
  const owner = { ingestId, emailId, companyId, requestId };
  const args = [{ companyId, skipLoader: true }];
  yield put({ type: 'gondola_ingestEmailSearch_STARTED', payload: owner, args });
  const request = { companyId, ...(ti2 ? { ti2 } : {}), criteria };
  const { response, error } = yield call(
    generativeAiApis.gondolaSearchIngestEmail,
    ingestId,
    emailId,
    request,
  );
  const currentCompanyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
  if (currentCompanyId !== companyId) {
    yield put({ type: 'gondola_ingestEmailSearch_CANCELLED', payload: owner, args });
    return;
  }
  if (error) {
    yield put({
      type: 'gondola_ingestEmailSearch_ERROR',
      payload: { ...owner, error: 'Unable to search bookings' },
      args,
    });
    return;
  }
  yield put({
    type: 'gondola_ingestEmailSearch_SUCCESS',
    payload: { ...owner, response },
    args,
  });
}

let ingestEmailSelectRequestSequence = 0;
const activeIngestEmailSelections = new Map();

export function* selectIngestEmail({ payload }) {
  const { ingestId, emailId, companyId, bookingId, app, hint } = payload;
  const requestKey = JSON.stringify([companyId, ingestId, emailId]);
  if (activeIngestEmailSelections.has(requestKey)) return;
  const requestId = ++ingestEmailSelectRequestSequence;
  const owner = { ingestId, emailId, companyId, requestId };
  const args = [{ companyId, skipLoader: true }];
  activeIngestEmailSelections.set(requestKey, requestId);

  try {
    yield put({ type: 'gondola_ingestEmailSelect_STARTED', payload: owner, args });
    const { response, error } = yield call(
      generativeAiApis.gondolaSelectIngestEmail,
      ingestId,
      emailId,
      { companyId, bookingId, app, hint },
    );
    const currentCompanyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
    if (currentCompanyId !== companyId
      || activeIngestEmailSelections.get(requestKey) !== requestId) {
      yield put({ type: 'gondola_ingestEmailSelect_CANCELLED', payload: owner, args });
      return;
    }
    if (error) {
      yield put({
        type: 'gondola_ingestEmailSelect_ERROR',
        payload: { ...owner, error: 'Unable to select booking' },
        args,
      });
      return;
    }
    const status = R.prop('status', response);
    const responseIngestId = R.prop('ingestId', response);
    const jobId = R.prop('jobId', response);
    const acceptedResponseIsValid = status === 'accepted'
      && responseIngestId === ingestId
      && typeof jobId === 'string'
      && Boolean(jobId);
    const associatedResponseIsValid = status === 'already_associated'
      && responseIngestId === ingestId;
    if (!acceptedResponseIsValid && !associatedResponseIsValid) {
      yield put({
        type: 'gondola_ingestEmailSelect_ERROR',
        payload: { ...owner, error: 'Unexpected selection response' },
        args,
      });
      return;
    }
    yield put({
      type: 'gondola_ingestEmailSelect_SUCCESS',
      payload: { ...owner, response },
      args,
    });
    if (status === 'accepted') {
      yield put({
        type: 'gondola_pollSelectedIngest',
        payload: { ...owner, jobId },
      });
      return;
    }
    yield call(fetchIngestEmails, { payload: { ingestId, companyId } });
    yield call(fetchIngestPartners, { payload: { ingestId, companyId } });
    yield put({ type: 'gondola_fetchIngests', payload: { companyId } });
  } finally {
    if (activeIngestEmailSelections.get(requestKey) === requestId) {
      activeIngestEmailSelections.delete(requestKey);
    }
  }
}

const TERMINAL_INGEST_STATUSES = new Set([
  'completed', 'partial', 'failed', 'cancelled', 'canceled', 'needs_review', 'review_required',
]);
const SELECTED_INGEST_POLL_ATTEMPTS = 120;

export function* pollSelectedIngest({ payload }) {
  const {
    ingestId, emailId, companyId, jobId, requestId,
  } = payload || {};
  if (!ingestId || !emailId || !companyId || !jobId) return;
  const owner = {
    ingestId,
    emailId,
    companyId,
    ...(requestId === undefined ? {} : { requestId }),
  };
  const args = [{ companyId, skipLoader: true }];

  for (let attempt = 0; attempt < SELECTED_INGEST_POLL_ATTEMPTS; attempt++) {
    let currentCompanyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
    if (currentCompanyId !== companyId) return;
    if (requestId !== undefined) {
      const currentRequestId = yield select(R.path([
        'generativeAi', 'gondola', 'ingestEmailsById', ingestId, 'selectByEmailId', emailId, 'requestId',
      ]));
      if (currentRequestId !== requestId) return;
    }

    const response = yield apiSaga(
      'gondola_ingests',
      generativeAiApis.gondolaGetIngests,
      [companyId, { skipLoader: true }],
    );

    currentCompanyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
    if (currentCompanyId !== companyId) return;
    if (requestId !== undefined) {
      const currentRequestId = yield select(R.path([
        'generativeAi', 'gondola', 'ingestEmailsById', ingestId, 'selectByEmailId', emailId, 'requestId',
      ]));
      if (currentRequestId !== requestId) return;
    }
    if (!response) {
      yield put({
        type: 'gondola_ingestEmailSelect_MONITORING_ERROR',
        payload: { ...owner, error: 'Unable to monitor ingestion' },
        args,
      });
      return;
    }

    const ingests = Array.isArray(R.prop('ingests', response)) ? response.ingests : [];
    const selectedIngest = ingests.find(ingest => (
      R.prop('ingestId', ingest) === ingestId && R.prop('jobId', ingest) === jobId
    ));
    const status = typeof R.prop('status', selectedIngest) === 'string'
      ? selectedIngest.status.toLowerCase()
      : '';

    if (TERMINAL_INGEST_STATUSES.has(status)) {
      if (isPendingManualSelection(selectedIngest, jobId)) {
        if (attempt < SELECTED_INGEST_POLL_ATTEMPTS - 1) yield delay(5e3);
        continue;
      }
      yield put({
        type: 'gondola_ingestEmailSelect_MONITORING_DONE',
        payload: { ...owner, terminalStatus: status },
        args,
      });
      yield call(fetchIngestEmails, { payload: { ingestId, companyId } });
      yield call(fetchIngestPartners, { payload: { ingestId, companyId } });
      yield put({ type: 'gondola_fetchIngests', payload: { companyId } });
      return;
    }
    if (status && !ACTIVE_INGEST_STATUSES.has(status)) {
      yield put({
        type: 'gondola_ingestEmailSelect_MONITORING_ERROR',
        payload: { ...owner, error: 'Unexpected ingestion status' },
        args,
      });
      return;
    }
    if (attempt < SELECTED_INGEST_POLL_ATTEMPTS - 1) yield delay(5e3);
  }

  yield put({
    type: 'gondola_ingestEmailSelect_MONITORING_ERROR',
    payload: { ...owner, error: 'Timed out waiting for ingestion' },
    args,
  });
}

export function* assignPartners({ payload }) {
  try {
    const result = yield apiSaga('gondola_assign', generativeAiApis.gondolaAssignPartners, [payload.ingestId, {
      companyId: payload.companyId,
      assignments: payload.assignments,
      createScopePerPartner: Boolean(payload.createScopePerPartner),
      ...(Array.isArray(payload.caseIds) ? { caseIds: payload.caseIds } : {}),
      skipLoader: true,
    }]);
    if (result && !result.assignmentOperationError) {
      yield put({ type: 'gondola_fetchIngestPartners', payload: { ingestId: payload.ingestId, companyId: payload.companyId } });
    }
    yield put({ type: 'gondola_fetchScopes', payload: { companyId: payload.companyId } });
  } catch (err) {
    console.debug('assignPartners error', err);
    yield put({
      type: 'gondola_assign_ERROR', payload: err.message,
      args: [payload.ingestId, { companyId: payload.companyId }],
    });
  }
}

function* triggerRun({ payload }) {
  yield apiSaga('gondola_startRun', generativeAiApis.gondolaStartRun, [payload, { skipLoader: true }]);
}

function* triggerDeltaSync({ payload }) {
  const response = yield apiSaga('gondola_deltaSyncRun', generativeAiApis.gondolaRunDeltaSync, [payload, { skipLoader: true }]);
  if (response) {
    yield put({ type: 'gondola_pollDeltaSyncStatus', payload: { awaitStart: true } });
  }
}

export function* triggerBatchPromotion({ payload }) {
  const response = yield apiSaga('gondola_batchPromotionRun', generativeAiApis.gondolaRunBatchPromotion, [payload, { skipLoader: true }]);
  if (response) yield put({ type: 'gondola_pollBatchPromotionStatus' });
}

export function* pollBatchPromotionStatus() {
  const companyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
  if (!companyId) return;
  const args = [{ companyId, skipLoader: true }];
  yield put({ type: 'gondola_batchPromotionPoll_STARTED', args });
  try {
    for (let attempt = 0; attempt < 720; attempt++) {
      const currentCompanyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
      if (currentCompanyId !== companyId) return;
      const response = yield apiSaga('gondola_batchPromotionStatus', generativeAiApis.gondolaGetBatchPromotionStatus, args);
      const responseCompanyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
      if (responseCompanyId !== companyId) return;
      if (!response) return;
      if (!R.path(['status', 'inProgress'], response)) {
        yield apiSaga('gondola_scopes', generativeAiApis.gondolaGetScopes, [companyId, { skipLoader: true }]);
        return;
      }
      if (attempt % 3 === 0) {
        yield apiSaga('gondola_scopes', generativeAiApis.gondolaGetScopes, [companyId, { skipLoader: true }]);
      }
      yield delay(5e3);
    }
    yield put({ type: 'gondola_batchPromotionPoll_TIMEOUT', args });
  } finally {
    yield put({ type: 'gondola_batchPromotionPoll_ENDED', args });
  }
}

function* refreshScopesAfterDeltaSync() {
  const companyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
  if (companyId) {
    yield apiSaga('gondola_scopes', generativeAiApis.gondolaGetScopes, [companyId, { skipLoader: true }]);
  }
}

function* pollDeltaSyncStatus({ payload } = {}) {
  const awaitStart = Boolean(payload && payload.awaitStart);
  const pollCompanyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
  if (!pollCompanyId) return;
  // Must skipLoader: globalUI only clears *_STARTED via *_SUCCESS/*_ERROR, not _ENDED.
  yield put({ type: 'gondola_deltaSyncPoll_STARTED', args: [{ companyId: pollCompanyId, skipLoader: true }] });
  let sawInProgress = false;
  let startGraceLeft = awaitStart ? 15 : 0;
  try {
    for (let i = 0; i < 180; i++) {
      const currentCompanyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
      if (currentCompanyId !== pollCompanyId) return;
      yield apiSaga('gondola_deltaSyncStatus', generativeAiApis.gondolaGetDeltaSyncStatus, [{ companyId: pollCompanyId, skipLoader: true }]);
      const inProgress = yield select(R.pathOr(false, ['generativeAi', 'gondola', 'deltaSyncStatus', 'inProgress']));
      if (inProgress) {
        sawInProgress = true;
        startGraceLeft = 0;
        yield delay(5e3);
        continue;
      }
      if (sawInProgress) break;
      // Status can lag behind run accept; keep retrying while awaiting start.
      if (startGraceLeft > 0) {
        startGraceLeft -= 1;
        yield delay(2e3);
        continue;
      }
      break;
    }
  } finally {
    yield put({ type: 'gondola_deltaSyncPoll_ENDED', args: [{ companyId: pollCompanyId, skipLoader: true }] });
  }

  const inProgress = yield select(R.pathOr(false, ['generativeAi', 'gondola', 'deltaSyncStatus', 'inProgress']));
  if (inProgress) {
    // Cap reached while still running — unlock UI, but keep a low-frequency
    // background watch going so scopes still refresh once it actually finishes,
    // even if the user stays on this page without navigating.
    yield put({ type: 'gondola_deltaSyncPoll_TIMEOUT' });
    yield spawn(watchDeltaSyncCompletion);
    return;
  }
  // Always refresh on a clean idle exit — covers nav-resume where the sync
  // finished between the location status fetch and this poll's first check
  // (sawInProgress never got set).
  yield call(refreshScopesAfterDeltaSync);
}

function* watchDeltaSyncCompletion() {
  const watchCompanyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
  if (!watchCompanyId) return;
  for (let i = 0; i < 60; i++) {
    // Check immediately on first iteration so we don't sit idle for 30s if the
    // job already finished right as the main poll timed out.
    if (i > 0) yield delay(30e3);
    const currentCompanyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
    if (currentCompanyId !== watchCompanyId) return;
    yield apiSaga('gondola_deltaSyncStatus', generativeAiApis.gondolaGetDeltaSyncStatus, [{ companyId: watchCompanyId, skipLoader: true }]);
    const inProgress = yield select(R.pathOr(false, ['generativeAi', 'gondola', 'deltaSyncStatus', 'inProgress']));
    if (!inProgress) {
      yield call(refreshScopesAfterDeltaSync);
      return;
    }
  }
}

export function* stopRun({ payload }) {
  yield apiSaga('gondola_stopRun', generativeAiApis.gondolaStopRun, [
    payload.runId,
    payload.contextScope,
    { skipLoader: true },
  ]);
}

export function* forceStopRun({ payload }) {
  yield apiSaga('gondola_forceStopRun', generativeAiApis.gondolaForceStopRun, [
    payload.runId,
    payload.contextScope,
    { skipLoader: true },
  ]);
}

export function* promoteRun({ payload }) {
  yield apiSaga('gondola_promoteRun', generativeAiApis.gondolaPromoteRun, [
    payload.runId,
    payload.contextScope,
    { skipLoader: true },
  ]);
}

export function* discardRun({ payload }) {
  yield apiSaga('gondola_discardRun', generativeAiApis.gondolaDiscardRun, [
    payload.runId,
    payload.contextScope,
    { skipLoader: true },
  ]);
}

export function* stopIngest({ payload }) {
  const { ingestId, companyId } = payload || {};
  if (!ingestId || !companyId) return;
  yield apiSaga('gondola_stopIngest', generativeAiApis.gondolaCancelIngest, [
    ingestId,
    { companyId, skipLoader: true },
  ]);
  yield put({ type: 'gondola_fetchIngests', payload: { companyId } });
}

function* fetchAppKeys() {
  const { companyId, ti2token } = yield call(_getAccountInfo);
  yield apiSaga('gondola_appKeys', ti2Apis.getAllAppKeys, [{ companyId, ti2token }, { skipLoader: true }]);
}

function* fetchRunDetail({ payload }) {
  const scopeId = yield select(R.path(['generativeAi', 'gondola', 'currentScope', 'scopeId']));
  yield apiSaga('gondola_runDetail', generativeAiApis.gondolaGetRunDetail, [
    scopeId,
    payload.runId,
    { contextScope: payload.contextScope, skipLoader: true },
  ]);
}

function* fetchArtifactFiles({ payload }) {
  const scopeId = yield select(R.path(['generativeAi', 'gondola', 'currentScope', 'scopeId']));
  yield apiSaga('gondola_artifacts', generativeAiApis.gondolaGetRunArtifacts, [
    scopeId,
    payload.runId,
    payload.iteration,
    null,
    { contextScope: payload.contextScope, skipLoader: true },
  ]);
}

function* fetchArtifactContent({ payload }) {
  const scopeId = yield select(R.path(['generativeAi', 'gondola', 'currentScope', 'scopeId']));
  yield apiSaga('gondola_artifactContent', generativeAiApis.gondolaGetRunArtifacts, [
    scopeId,
    payload.runId,
    payload.iteration,
    payload.file,
    { contextScope: payload.contextScope, skipLoader: true },
  ]);
}

function* fetchScopeFiles({ payload }) {
  const scopeId = yield select(R.path(['generativeAi', 'gondola', 'currentScope', 'scopeId']));
  if (!scopeId || (payload.scopeId && payload.scopeId !== scopeId)) return;
  yield apiSaga('gondola_scopeFiles', generativeAiApis.gondolaGetScopeFiles, [
    scopeId,
    { contextScope: payload.contextScope, skipLoader: true },
  ]);
}

function* fetchScopeMapping({ payload }) {
  const scopeId = yield select(R.path(['generativeAi', 'gondola', 'currentScope', 'scopeId']));
  if (!scopeId || (payload.scopeId && payload.scopeId !== scopeId)) return;
  yield apiSaga('gondola_scopeMapping', generativeAiApis.gondolaGetScopeMapping, [
    scopeId,
    { contextScope: payload.contextScope, skipLoader: true },
  ]);
}

function* fetchScopeRuns({ payload }) {
  const scopeId = yield select(R.path(['generativeAi', 'gondola', 'currentScope', 'scopeId']));
  if (!scopeId || (payload.scopeId && payload.scopeId !== scopeId)) return;
  yield apiSaga('gondola_scopeRuns', generativeAiApis.gondolaGetScopeRuns, [
    scopeId,
    payload.limit || 30,
    payload.cursor || null,
    { contextScope: payload.contextScope, skipLoader: true },
  ]);
}

function* fetchScopeCounts({ payload }) {
  const scopeId = yield select(R.path(['generativeAi', 'gondola', 'currentScope', 'scopeId']));
  if (!scopeId || (payload.scopeId && payload.scopeId !== scopeId)) return;
  yield apiSaga('gondola_scopeCounts', generativeAiApis.gondolaGetScopeCounts, [
    scopeId,
    { contextScope: payload.contextScope, skipLoader: true },
  ]);
}

function* fetchScopeFile({ payload }) {
  const scopeId = yield select(R.path(['generativeAi', 'gondola', 'currentScope', 'scopeId']));
  yield apiSaga('gondola_scopeFile', generativeAiApis.getScopeFile, [
    scopeId,
    payload.fileName,
    { contextScope: payload.contextScope, skipLoader: true },
  ]);
}

function* saveScopeFile({ payload }) {
  const scopeId = yield select(R.path(['generativeAi', 'gondola', 'currentScope', 'scopeId']));
  const metadata = { contextScope: payload.contextScope, skipLoader: true };
  yield apiSaga('gondola_scopeFileSave', generativeAiApis.saveScopeFile, [scopeId, payload.fileName, payload.content, metadata]);
  yield apiSaga('gondola_scopeFiles', generativeAiApis.gondolaGetScopeFiles, [scopeId, metadata]);
}


function* fetchScopeAssociations({ payload }) {
  const scopeId = yield select(R.path(['generativeAi', 'gondola', 'currentScope', 'scopeId']));
  if (!scopeId) return;
  const contextScope = `${payload.companyId}/${payload.scopeId}`;
  if (payload.scopeId !== scopeId) return;
  yield apiSaga('gondola_scopeMapping', generativeAiApis.gondolaGetScopeMapping, [
    scopeId,
    { contextScope, skipLoader: true },
  ]);
}

function* addScopeAssociation({ payload }) {
  yield put({ type: 'gondola_addAssociation_STARTED', args: [{ skipLoader: true }] });
  const { response, error } = yield call(generativeAiApis.gondolaAddScopeAssociation, payload);
  if (error) {
    yield put({ type: 'gondola_addAssociation_ERROR', payload: error.message || error });
    return;
  }
  if (response && response.requiresConfirmation) {
    yield put({
      type: 'gondola_addAssociation_NEEDS_CONFIRM',
      payload: { request: payload, currentAssociation: response.currentAssociation },
    });
    return;
  }
  yield put({ type: 'gondola_addAssociation_SUCCESS', payload: response });
  yield put({ type: 'gondola_fetchScopeAssociations', payload: { scopeId: payload.scopeId, companyId: payload.companyId } });
  if (response && response.reassignedFrom) {
    yield put({ type: 'gondola_fetchScopes', payload: { companyId: payload.companyId } });
  }
}

function* removeScopeAssociation({ payload }) {
  yield apiSaga('gondola_removeAssociation', generativeAiApis.gondolaRemoveScopeAssociation,
    [payload, { skipLoader: true }]);
  yield put({ type: 'gondola_fetchScopeAssociations', payload: { scopeId: payload.scopeId, companyId: payload.companyId } });
  yield put({ type: 'gondola_fetchScopes', payload: { companyId: payload.companyId } });
}

function* addScopeTi2({ payload }) {
  const { response } = yield apiSaga('gondola_addScopeTi2', generativeAiApis.gondolaAddScopeTi2,
    [payload]);
  if (response) {
    yield put({ type: 'gondola_scopeTi2s_updated', payload: response.ti2s });
  }
  const scopeId = payload.contextScope && payload.contextScope.split('/').pop();
  yield put({
    type: 'gondola_fetchScopeMapping',
    payload: { scopeId, contextScope: payload.contextScope },
  });
}

function* removeScopeTi2({ payload }) {
  const { response } = yield apiSaga('gondola_removeScopeTi2', generativeAiApis.gondolaRemoveScopeTi2,
    [payload]);
  if (response) {
    yield put({ type: 'gondola_scopeTi2s_updated', payload: response.ti2s });
  }
  const scopeId = payload.contextScope && payload.contextScope.split('/').pop();
  yield put({
    type: 'gondola_fetchScopeMapping',
    payload: { scopeId, contextScope: payload.contextScope },
  });
}

function* copyScope({ payload }) {
  yield apiSaga('gondola_copyScope', generativeAiApis.gondolaCopyScope, [payload, { skipLoader: true }]);
  yield put({ type: 'gondola_fetchScopes', payload: { companyId: payload.companyId } });
}

function* deleteScope({ payload }) {
  yield apiSaga('gondola_deleteScope', generativeAiApis.gondolaDeleteScope, [payload, { skipLoader: true }]);
  yield put({ type: 'gondola_fetchScopes', payload: { companyId: payload.companyId } });
}

const scopeOwnerSelector = state => ({
  companyId: R.pathOr('', ['account', 'company', 'companyId'], state),
  scopeId: R.path(['generativeAi', 'gondola', 'currentScope', 'scopeId'], state),
  contextScope: R.path(['generativeAi', 'gondola', 'currentScope', 'contextScope'], state),
});

const isScopeOwner = (owner, companyId, scopeId, contextScope) => (
  owner.companyId === companyId
  && owner.scopeId === scopeId
  && (!owner.contextScope || owner.contextScope === contextScope)
);

export function* onRunSuccess({ payload, args } = {}) {
  const firstArg = R.pathOr({}, [0], args);
  const secondArg = R.pathOr({}, [1], args);
  const launchRequest = typeof firstArg === 'string' && typeof secondArg === 'object'
    ? secondArg
    : firstArg;
  const launchContextScope = typeof secondArg === 'string'
    ? secondArg
    : R.prop('contextScope', launchRequest);
  const launchCompanyId = R.prop('companyId', launchRequest)
    || (launchContextScope && launchContextScope.split('/')[0]);
  const launchScopeId = launchContextScope && launchContextScope.split('/').pop();
  const runId = payload && payload.runId;
  const celeryJobId = payload && payload.celeryJobId;

  if (celeryJobId && launchContextScope && launchCompanyId && launchScopeId) {
    let owner = yield select(scopeOwnerSelector);
    if (!isScopeOwner(owner, launchCompanyId, launchScopeId, launchContextScope)) return;

    yield put({
      type: 'gondola_runPollingStarted',
      payload: { contextScope: launchContextScope, runId, celeryJobId },
    });
    yield apiSaga('gondola_scopes', generativeAiApis.gondolaGetScopes, [launchCompanyId, { skipLoader: true }]);
    owner = yield select(scopeOwnerSelector);
    if (!isScopeOwner(owner, launchCompanyId, launchScopeId, launchContextScope)) return;

    yield apiSaga('gondola_scopeDetail', generativeAiApis.gondolaGetScopeDetail, [
      launchScopeId,
      { contextScope: launchContextScope, skipLoader: true },
    ]);
    yield put({
      type: 'gondola_fetchScopeRuns',
      payload: { scopeId: launchScopeId, contextScope: launchContextScope, limit: 30 },
    });
    yield put({
      type: 'gondola_fetchScopeCounts',
      payload: { scopeId: launchScopeId, contextScope: launchContextScope },
    });
    owner = yield select(scopeOwnerSelector);
    if (!isScopeOwner(owner, launchCompanyId, launchScopeId, launchContextScope)) return;

    yield put({
      type: 'gondola_pollActiveJob',
      payload: { contextScope: launchContextScope, celeryJobId },
    });
    return;
  }

  const companyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
  if (companyId) {
    yield apiSaga('gondola_scopes', generativeAiApis.gondolaGetScopes, [companyId, { skipLoader: true }]);
  }
  const currentScopeId = yield select(R.path(['generativeAi', 'gondola', 'currentScope', 'scopeId']));
  if (currentScopeId) {
    if (companyId && celeryJobId) {
      const contextScope = `${companyId}/${currentScopeId}`;
      yield apiSaga('gondola_scopeDetail', generativeAiApis.gondolaGetScopeDetail, [
        currentScopeId,
        { contextScope, skipLoader: true },
      ]);
      yield put({
        type: 'gondola_fetchScopeRuns',
        payload: { scopeId: currentScopeId, contextScope, limit: 30 },
      });
      yield put({
        type: 'gondola_fetchScopeCounts',
        payload: { scopeId: currentScopeId, contextScope },
      });
      yield put({
        type: 'gondola_pollActiveJob',
        payload: { contextScope, celeryJobId },
      });
    } else {
      // Backward compatibility for older responses: rediscover the persisted
      // Celery ID through refreshed scope activity.
      yield put({
        type: 'gondola_enterScope',
        payload: { scopeId: currentScopeId, contextScope: `${companyId}/${currentScopeId}` },
      });
    }
  }
}

function* onDeltaSyncSuccess() {
  const companyId = yield select(R.pathOr('', ['account', 'company', 'companyId']));
  if (companyId) {
    yield apiSaga('gondola_scopes', generativeAiApis.gondolaGetScopes, [companyId, { skipLoader: true }]);
  }
}

// eslint-disable-next-line import/no-anonymous-default-export
export default [
  takeLatest('@@router/LOCATION_CHANGE', onLocationChange),
  takeLatest('gondola_enterScope', handleEnterScope),
  takeLatest('gondola_pollActiveJob', handlePollActiveJob),
  takeLatest('gondola_fetchScopes', fetchScopes),
  takeLatest('gondola_createScope', createScope),
  takeLatest('gondola_saveScopeAutomation', saveScopeAutomation),
  takeLatest('gondola_fetchIngests', fetchIngests),
  takeLatest('gondola_startIngestion', startIngestion),
  takeEvery('gondola_stopIngest', stopIngest),
  takeLatest('gondola_pollIngests', pollIngests),
  takeLatest('gondola_fetchIngestPartners', fetchIngestPartners),
  takeLatest('gondola_fetchIngestEmails', fetchIngestEmails),
  takeEvery('gondola_fetchIngestEmailDetail', fetchIngestEmailDetail),
  takeEvery('gondola_searchIngestEmail', searchIngestEmail),
  takeEvery('gondola_selectIngestEmail', selectIngestEmail),
  takeEvery('gondola_pollSelectedIngest', pollSelectedIngest),
  takeLatest('gondola_assignPartners', assignPartners),
  takeLatest('gondola_triggerRun', triggerRun),
  takeLatest('gondola_triggerDeltaSync', triggerDeltaSync),
  takeLatest('gondola_triggerBatchPromotion', triggerBatchPromotion),
  takeLatest('gondola_pollBatchPromotionStatus', pollBatchPromotionStatus),
  takeLatest('gondola_pollDeltaSyncStatus', pollDeltaSyncStatus),
  takeLatest('gondola_stopRun', stopRun),
  takeLatest('gondola_forceStopRun', forceStopRun),
  takeLatest('gondola_promoteRun', promoteRun),
  takeLatest('gondola_discardRun', discardRun),
  takeLatest('gondola_startRun_SUCCESS', onRunSuccess),
  takeLatest('gondola_deltaSyncRun_SUCCESS', onDeltaSyncSuccess),
  takeLatest('gondola_stopRun_SUCCESS', onRunSuccess),
  takeLatest('gondola_forceStopRun_SUCCESS', onRunSuccess),
  takeLatest('gondola_promoteRun_SUCCESS', onRunSuccess),
  takeLatest('gondola_discardRun_SUCCESS', onRunSuccess),
  takeLatest('gondola_fetchAppKeys', fetchAppKeys),
  takeLatest('gondola_fetchRunDetail', fetchRunDetail),
  takeLatest('gondola_fetchArtifacts', fetchArtifactFiles),
  takeLatest('gondola_fetchArtifactContent', fetchArtifactContent),
  takeLatest('gondola_fetchScopeFiles', fetchScopeFiles),
  takeLatest('gondola_fetchScopeMapping', fetchScopeMapping),
  takeLatest('gondola_fetchScopeRuns', fetchScopeRuns),
  takeLatest('gondola_fetchScopeCounts', fetchScopeCounts),
  takeLatest('gondola_fetchScopeFile', fetchScopeFile),
  takeLatest('gondola_saveScopeFile', saveScopeFile),
  takeLatest('gondola_fetchScopeAssociations', fetchScopeAssociations),
  takeLatest('gondola_addScopeAssociation', addScopeAssociation),
  takeLatest('gondola_removeScopeAssociation', removeScopeAssociation),
  takeLatest('gondola_addScopeTi2', addScopeTi2),
  takeLatest('gondola_removeScopeTi2', removeScopeTi2),
  takeLatest('gondola_copyScope', copyScope),
  takeLatest('gondola_deleteScope', deleteScope),
];
